// One-shot v2 migration: localStorage chats → server.
//
// Pre-v2 the SPA stored chat history in `localStorage['malpedia-sessions']`
// including heavy per-message events (planning/profile/graph_traversal/
// IOCs) and the per-session accumulated graph. After hitting the ~5 MB
// origin quota we made the server authoritative.
//
// This module runs once per browser. On first load with v2 client code:
//   1. If `malpedia-sessions-migrated === 'v2'` → short-circuit, no work.
//   2. Otherwise: read the legacy blob, build an import payload that
//      carries the heavy fields (so history is not silently truncated),
//      POST /chats/import, and on success:
//        - remove `malpedia-sessions`
//        - set `malpedia-sessions-migrated=v2`
//      Subsequent loads will short-circuit at step 1.
//
// The legacy reader stays one release past the flag so a user who
// downgrades briefly doesn't lose their history. Remove
// `_legacyReadSessions` two releases out.

import type { StoredMessage } from '../types';
import {
  importChats,
  type ImportChatPayload,
  type ImportFolderPayload,
  type ImportTurnPayload,
} from './feedbackApi';

const LEGACY_SESSIONS_KEY = 'malpedia-sessions';
const LEGACY_FOLDERS_KEY = 'malpedia-folders';
const LEGACY_FOLDER_COLLAPSE_KEY = 'malpedia-folder-collapse';
const MIGRATION_FLAG_KEY = 'malpedia-sessions-migrated';
const MIGRATION_FLAG_VALUE = 'v2';

const CHUNK_MAX_CHATS = 50;
const CHUNK_MAX_TURNS = 2000;

// Loose stand-in for the v1 ChatSession shape — fields we still read.
// Typed as `any` access on purpose: the legacy blob may have older shapes.
interface LegacyChatSession {
  id: string;
  title?: string;
  createdAt?: number;
  updatedAt?: number;
  messages: StoredMessage[];
  accumulatedGraph?: unknown;
  folderId?: string;
  temporary?: boolean;
  serverChatId?: string;
  serverIsSensitive?: boolean;
}

interface LegacyFolder {
  id: string;
  name: string;
  createdAt?: number;
}

function _legacyReadSessions(): LegacyChatSession[] {
  try {
    const raw = localStorage.getItem(LEGACY_SESSIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as LegacyChatSession[]) : [];
  } catch {
    return [];
  }
}

function _legacyReadFolders(): LegacyFolder[] {
  try {
    const raw = localStorage.getItem(LEGACY_FOLDERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as LegacyFolder[]) : [];
  } catch {
    return [];
  }
}

function _legacyReadCollapse(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(LEGACY_FOLDER_COLLAPSE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function pairTurns(
  messages: StoredMessage[],
): Array<{ user: StoredMessage; assistant: StoredMessage }> {
  const pairs: Array<{ user: StoredMessage; assistant: StoredMessage }> = [];
  let i = 0;
  while (i < messages.length) {
    const m = messages[i];
    if (m.role === 'user' && messages[i + 1]?.role === 'assistant') {
      pairs.push({ user: m, assistant: messages[i + 1] });
      i += 2;
    } else {
      // Drop orphans (trailing user with no answer, stray assistant).
      i += 1;
    }
  }
  return pairs;
}

function interpolateTimestamp(
  startMs: number,
  endMs: number,
  index: number,
  total: number,
): string {
  if (total <= 1) return new Date(startMs).toISOString();
  const t = startMs + ((endMs - startMs) * index) / (total - 1);
  return new Date(t).toISOString();
}

function sessionToImportPayload(s: LegacyChatSession): ImportChatPayload | null {
  const pairs = pairTurns(s.messages);
  if (pairs.length === 0) return null;
  const startMs = s.createdAt ?? Date.now();
  const endMs = Math.max(s.updatedAt ?? startMs, startMs);

  const turns: ImportTurnPayload[] = pairs.map((pair, idx) => ({
    turn_id: pair.assistant.turnId ?? crypto.randomUUID(),
    seq: idx + 1,
    query: pair.user.content,
    answer: pair.assistant.content,
    sources: pair.assistant.sources ?? null,
    plan: null,
    model_name: null,
    // Heavy event fields — these are the localStorage bloat we're
    // moving server-side. Carry them across so history isn't lost.
    routing_event: pair.assistant.routingEvent ?? null,
    planning_event: pair.assistant.planningEvent ?? null,
    profile_event: pair.assistant.profileEvent ?? null,
    graph_traversal: pair.assistant.graphTraversalEvent ?? null,
    step_details: pair.assistant.stepDetails
      ? {
          completed_steps: pair.assistant.completedSteps ?? [],
          step_details: pair.assistant.stepDetails,
          score_threshold: pair.assistant.scoreThreshold ?? null,
        }
      : null,
    detected_iocs:
      pair.assistant.queryDetectedIocs || pair.assistant.responseDetectedIocs
        ? {
            query: pair.assistant.queryDetectedIocs ?? [],
            response: pair.assistant.responseDetectedIocs ?? [],
          }
        : null,
    created_at: interpolateTimestamp(startMs, endMs, idx, pairs.length),
  }));

  return {
    chat_id: s.serverChatId ?? s.id,
    title: s.title ?? '',
    is_sensitive: s.serverIsSensitive ?? false,
    folder_id: s.folderId ?? null,
    // Per-session accumulated investigation graph — the other large blob.
    accumulated_graph: s.accumulatedGraph ?? null,
    created_at: new Date(startMs).toISOString(),
    updated_at: new Date(endMs).toISOString(),
    turns,
  };
}

function chunkPayloads(payloads: ImportChatPayload[]): ImportChatPayload[][] {
  const chunks: ImportChatPayload[][] = [];
  let current: ImportChatPayload[] = [];
  let currentTurns = 0;

  for (const p of payloads) {
    const turnCount = p.turns.length;
    const wouldExceed =
      current.length >= CHUNK_MAX_CHATS ||
      currentTurns + turnCount > CHUNK_MAX_TURNS;
    if (wouldExceed && current.length > 0) {
      chunks.push(current);
      current = [];
      currentTurns = 0;
    }
    current.push(p);
    currentTurns += turnCount;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

export interface MigrationResult {
  ran: boolean;       // false if the flag short-circuited
  imported: number;
  skipped: number;
}

/** One-shot migration. Returns `{ran:false}` immediately if the v2 flag
 *  is already set. On success removes the legacy keys and sets the flag,
 *  so the next call is a no-op. */
export async function runV2Migration(): Promise<MigrationResult> {
  if (typeof localStorage === 'undefined') {
    return { ran: false, imported: 0, skipped: 0 };
  }
  if (localStorage.getItem(MIGRATION_FLAG_KEY) === MIGRATION_FLAG_VALUE) {
    return { ran: false, imported: 0, skipped: 0 };
  }

  const sessions = _legacyReadSessions();
  const folders = _legacyReadFolders();
  const collapse = _legacyReadCollapse();

  // Nothing to migrate — still set the flag so we don't reread the empty
  // blob on every mount.
  if (sessions.length === 0 && folders.length === 0 && Object.keys(collapse).length === 0) {
    localStorage.removeItem(LEGACY_SESSIONS_KEY);
    localStorage.removeItem(LEGACY_FOLDERS_KEY);
    localStorage.removeItem(LEGACY_FOLDER_COLLAPSE_KEY);
    localStorage.setItem(MIGRATION_FLAG_KEY, MIGRATION_FLAG_VALUE);
    return { ran: true, imported: 0, skipped: 0 };
  }

  const importFolders: ImportFolderPayload[] = folders.map((f) => ({
    folder_id: f.id,
    name: f.name,
    created_at: new Date(f.createdAt ?? Date.now()).toISOString(),
  }));

  const payloads: ImportChatPayload[] = [];
  for (const s of sessions) {
    if (s.temporary) continue;
    const p = sessionToImportPayload(s);
    if (p) payloads.push(p);
  }

  let imported = 0;
  let skipped = 0;
  const chunks = chunkPayloads(payloads);
  const includesFolders =
    importFolders.length > 0 || Object.keys(collapse).length > 0;

  try {
    if (chunks.length === 0 && includesFolders) {
      const res = await importChats({
        chats: [],
        folders: importFolders,
        folder_collapse: collapse,
      });
      imported += res.imported;
      skipped += res.skipped;
    }
    for (let i = 0; i < chunks.length; i++) {
      const body =
        i === 0 && includesFolders
          ? { chats: chunks[i], folders: importFolders, folder_collapse: collapse }
          : { chats: chunks[i] };
      const res = await importChats(body);
      imported += res.imported;
      skipped += res.skipped;
    }
  } catch (err) {
    // Migration failed mid-way. Leave the legacy keys and flag in place;
    // the next mount will retry. import is idempotent (INSERT OR IGNORE).
    console.warn('v2 migration: import failed; will retry on next load', err);
    return { ran: true, imported, skipped };
  }

  // Migration succeeded — drop legacy keys and set the flag so we never
  // run this code path again on this browser profile.
  localStorage.removeItem(LEGACY_SESSIONS_KEY);
  localStorage.removeItem(LEGACY_FOLDERS_KEY);
  localStorage.removeItem(LEGACY_FOLDER_COLLAPSE_KEY);
  localStorage.setItem(MIGRATION_FLAG_KEY, MIGRATION_FLAG_VALUE);
  return { ran: true, imported, skipped };
}
